import { useEffect, useState } from 'react'
import {
  getChatbotLogs,
  getDropOffAnalysis,
  getUnansweredQuestions,
  ChatbotLog,
} from '@/services/chatbot-analytics'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Loader2, MessageSquare, AlertCircle, BarChart, X } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from '@/components/ui/chart'
import {
  Bar,
  BarChart as RechartsBarChart,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts'
import { format } from 'date-fns'

const ChatbotAnalytics = () => {
  const [loading, setLoading] = useState(true)
  const [logs, setLogs] = useState<ChatbotLog[]>([])
  const [unanswered, setUnanswered] = useState<ChatbotLog[]>([])
  const [dropOffData, setDropOffData] = useState<
    { step: string; count: number }[]
  >([])

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const [logsRes, unansweredRes, dropOffRes] = await Promise.all([
          getChatbotLogs(),
          getUnansweredQuestions(),
          getDropOffAnalysis(),
        ])
        setLogs(logsRes.data || [])
        setUnanswered(unansweredRes.data || [])
        setDropOffData(dropOffRes.data || [])
      } catch (error) {
        console.error('Failed to fetch analytics', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  // Frequent Topics Calculation (Client-side simple word cloud approximation)
  const frequentTopics = logs
    .filter((l) => l.user_message)
    .flatMap((l) => l.user_message?.toLowerCase().split(/\s+/) || [])
    .filter((word) => word.length > 4) // Filter out small words
    .reduce(
      (acc, word) => {
        acc[word] = (acc[word] || 0) + 1
        return acc
      },
      {} as Record<string, number>,
    )

  const topTopics = Object.entries(frequentTopics)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10)

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold text-slate-900">Chatbot Analytics</h1>
        <p className="text-slate-500">
          Insights from your AI assistant interactions.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Interactions
            </CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{logs.length}</div>
            <p className="text-xs text-muted-foreground">Recorded messages</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Unanswered Questions
            </CardTitle>
            <AlertCircle className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">
              {unanswered.length}
            </div>
            <p className="text-xs text-muted-foreground">Requires attention</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Completed Funnels
            </CardTitle>
            <BarChart className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {dropOffData.find((d) => d.step === 'DONE')?.count || 0}
            </div>
            <p className="text-xs text-muted-foreground">Reached end of flow</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Drop-off Chart */}
        <Card className="col-span-1 lg:col-span-2">
          <CardHeader>
            <CardTitle>Drop-off Analysis</CardTitle>
            <CardDescription>
              User retention through diagnostic steps
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full">
              <ChartContainer
                config={{
                  count: { label: 'Users', color: 'hsl(var(--primary))' },
                }}
                className="h-full w-full"
              >
                <RechartsBarChart data={dropOffData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="step"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={10}
                    tickFormatter={(val) => val.slice(0, 10)}
                  />
                  <YAxis tickLine={false} axisLine={false} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar
                    dataKey="count"
                    fill="var(--color-count)"
                    radius={[4, 4, 0, 0]}
                  />
                </RechartsBarChart>
              </ChartContainer>
            </div>
          </CardContent>
        </Card>

        {/* Frequent Topics */}
        <Card>
          <CardHeader>
            <CardTitle>Frequent Topics</CardTitle>
            <CardDescription>
              Most common words in user messages
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {topTopics.map(([word, count]) => (
                <div
                  key={word}
                  className="bg-slate-100 text-slate-800 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2"
                >
                  {word}
                  <span className="bg-white px-1.5 py-0.5 rounded-full text-xs text-slate-500 border">
                    {count}
                  </span>
                </div>
              ))}
              {topTopics.length === 0 && (
                <p className="text-slate-500 text-sm">No data available yet.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Unanswered Questions Table */}
        <Card>
          <CardHeader>
            <CardTitle>Unanswered Questions</CardTitle>
            <CardDescription>
              Inputs that triggered fallback responses
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-h-[300px] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Message</TableHead>
                    <TableHead className="w-[100px]">Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unanswered.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        className="text-center text-muted-foreground py-8"
                      >
                        Great! No unanswered questions.
                      </TableCell>
                    </TableRow>
                  ) : (
                    unanswered.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell className="font-medium">
                          {log.user_message}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {format(new Date(log.created_at), 'MM/dd HH:mm')}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default ChatbotAnalytics
